import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Power, ShieldAlert, ExternalLink } from "lucide-react";
import { Input, Area, ImageInput, Toggle } from "./fields";
import { HelpTip } from "./HelpTip";
import { getMaintenance, updateMaintenance } from "@/lib/api";

const ColorField = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>
    <div className="mt-2 flex items-center gap-2">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="h-9 w-12 rounded bg-transparent border border-white/10 cursor-pointer" />
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none" />
    </div>
  </label>
);

export const AdminMaintenance = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  useEffect(() => { (async () => { try { setCfg(await getMaintenance()); } catch { toast.error("Chargement impossible"); } })(); }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      const payload = {
        enabled: next.enabled, title: next.title || "", message: next.message || "", logo: next.logo || "",
        bg: next.bg || "#050505", textColor: next.textColor || "#ffffff", accentColor: next.accentColor || "#3E9BFF",
        showSocials: !!next.showSocials, showContact: !!next.showContact, contactEmail: next.contactEmail || "",
      };
      await updateMaintenance(payload);
      return true;
    } catch { toast.error("Échec de l'enregistrement"); return false; }
    finally { setSaving(false); }
  };

  const toggle = async () => {
    const target = !cfg.enabled;
    if (target && !window.confirm("Activer le MODE MAINTENANCE ? Le site public affichera la page « indisponible ». Vous (admin connecté) continuerez à voir et tester le vrai site normalement.")) return;
    const next = { ...cfg, enabled: target };
    setCfg(next);
    const ok = await persist(next);
    if (ok) toast.success(target ? "Mode maintenance ACTIVÉ (instantané)" : "Site remis EN LIGNE");
  };

  const saveCustomization = async () => { if (await persist(cfg)) toast.success("Page de maintenance enregistrée"); };

  if (!cfg) return <div className="flex items-center gap-2 text-secondary2"><Loader2 className="animate-spin" size={16} /> Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="admin-maintenance">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-1">Site</div>
        <h2 className="font-serif text-3xl text-white flex items-center gap-3">Mode maintenance</h2>
      </div>

      {/* Big status toggle */}
      <div className={`rounded-xl border p-5 flex items-center justify-between gap-4 flex-wrap ${cfg.enabled ? "border-amber-400/40 bg-amber-400/5" : "border-electric/30 bg-electric/5"}`}>
        <div className="flex items-center gap-3">
          <span className={`h-3 w-3 rounded-full ${cfg.enabled ? "bg-amber-400 animate-pulse" : "bg-electric"}`} />
          <div>
            <div className="text-white font-semibold">{cfg.enabled ? "🔴 Mode maintenance actif" : "🟢 Site en ligne"}</div>
            <div className="text-[11px] text-secondary2">{cfg.enabled ? "Le public voit la page « indisponible ». Vous voyez le vrai site." : "Le site est accessible à tous."}</div>
          </div>
        </div>
        <button onClick={toggle} disabled={saving} data-testid="maintenance-toggle-btn"
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs uppercase tracking-[0.1em] font-semibold transition-colors disabled:opacity-60 ${cfg.enabled ? "bg-electric text-black hover:bg-white" : "bg-amber-400 text-black hover:bg-amber-300"}`}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />} {cfg.enabled ? "Remettre le site en ligne" : "Activer la maintenance"}
        </button>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-3 flex items-start gap-2 text-xs text-secondary2">
        <ShieldAlert size={15} className="text-electric mt-0.5 shrink-0" />
        <span>Sécurité : l'administration (<code className="text-white/70">/admin</code>) reste toujours accessible, même en maintenance — vous ne pouvez pas vous bloquer. L'activation/désactivation est <strong className="text-white/80">instantanée</strong>, sans redéploiement.
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-electric ml-1">Prévisualiser le site <ExternalLink size={11} /></a>
        </span>
      </div>

      {/* Customization */}
      <div className="space-y-4 border-t border-white/10 pt-5">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric inline-flex items-center gap-2">Page de maintenance <HelpTip text="Personnalisez ce que voient les visiteurs pendant la maintenance : logo, titre, message, couleurs, réseaux sociaux et bouton de contact." /></span>
        <ImageInput label="Logo (optionnel — sinon « KARAMS STUDIO » en texte)" value={cfg.logo} onChange={(v) => set("logo", v)} />
        <Input label="Titre" value={cfg.title} onChange={(v) => set("title", v)} testid="maintenance-title-input" />
        <Area label="Message" value={cfg.message} onChange={(v) => set("message", v)} rows={3} testid="maintenance-message-input" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ColorField label="Couleur de fond" value={cfg.bg} onChange={(v) => set("bg", v)} testid="maintenance-bg" />
          <ColorField label="Couleur du texte" value={cfg.textColor} onChange={(v) => set("textColor", v)} />
          <ColorField label="Couleur d'accent" value={cfg.accentColor} onChange={(v) => set("accentColor", v)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Toggle label="Afficher les réseaux sociaux" hint="Récupérés depuis vos Coordonnées (un réseau sans lien n'apparaît pas)." checked={!!cfg.showSocials} onChange={(v) => set("showSocials", v)} />
          <Toggle label="Afficher un bouton de contact" checked={!!cfg.showContact} onChange={(v) => set("showContact", v)} />
        </div>
        {cfg.showContact && <Input label="E-mail de contact (vide = e-mail des Coordonnées)" value={cfg.contactEmail} onChange={(v) => set("contactEmail", v)} />}
        <button onClick={saveCustomization} disabled={saving} data-testid="maintenance-save-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-xs uppercase tracking-[0.1em] font-semibold hover:border-electric hover:text-electric disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : null} Enregistrer la page de maintenance
        </button>
      </div>
    </div>
  );
};
